/*
 * Fleet reports + Dashboard pins (NEXUS-1384).
 *
 * The assistant and the pinned Dashboard cards both run the SAME named reports through
 * aquastarAdminReport — the assistant only ever picks a report id and a day window. A pin is
 * { report, params }; the card re-runs it on Dashboard load and on its refresh button.
 */

import { getFn, postFn } from './apiClient';
import { runSupportIssues } from './supportIssues';

export type ReportId =
  | 'faults_by_pump'
  | 'faults_by_code'
  | 'faults_per_day'
  | 'commands_by_user'
  | 'pump_overview'
  | 'pumps_offline'
  | 'firmware_versions'
  | 'firmware_updates'
  | 'open_faults'
  | 'support_issues';

export const REPORT_IDS: ReportId[] = [
  'faults_by_pump',
  'faults_by_code',
  'faults_per_day',
  'commands_by_user',
  'pump_overview',
  'pumps_offline',
  'firmware_versions',
  'firmware_updates',
  'open_faults',
  'support_issues',
];

/** Reports that run in the console, not on the server (so they cannot be pinned yet). */
export const CLIENT_REPORTS: ReadonlySet<ReportId> = new Set<ReportId>(['support_issues']);

export function isPinnable(report: ReportId): boolean {
  return !CLIENT_REPORTS.has(report);
}

export interface ReportParams {
  days?: number;
  hours?: number;
  pump?: string;
}

export interface ReportColumn {
  key: string;
  label: string;
  type: 'text' | 'number' | 'datetime' | 'date';
  /** Render the cell as a link to that pump / user; row[idKey] is the id. */
  link?: { kind: 'pump' | 'user' | 'issue'; idKey: string };
}

export interface ReportChart {
  type: 'bar' | 'line';
  xKey: string;
  yKeys: Array<{ key: string; label: string }>;
}

export interface ReportResult {
  report: ReportId;
  title: string;
  subtitle?: string;
  params: ReportParams;
  columns: ReportColumn[];
  rows: Array<Record<string, string | number | null>>;
  chart: ReportChart | null;
  generatedAt: string;
}

export interface DashboardPin {
  id: string;
  title: string | null;
  report: ReportId;
  params: ReportParams;
  chartType: 'bar' | 'line' | 'table' | null;
  createdAt: string | null;
  createdBy: string | null;
}

/** Fired on window when a pin is added or removed, so an open Dashboard reloads its cards. */
export const PINS_CHANGED_EVENT = 'aquastar:dashboard-pins-changed';

export function isReportId(v: unknown): v is ReportId {
  return typeof v === 'string' && (REPORT_IDS as string[]).includes(v);
}

export function runReport(report: ReportId, params: ReportParams = {}): Promise<ReportResult> {
  if (report === 'support_issues') return runSupportIssues(params);
  return getFn<ReportResult>('aquastarAdminReport', { report, days: params.days, hours: params.hours, pump: params.pump || undefined });
}

export async function listPins(): Promise<DashboardPin[]> {
  const r = await getFn<{ pins: DashboardPin[] }>('aquastarDashboardPins');
  return r.pins ?? [];
}

export async function addPin(input: {
  report: ReportId;
  params: ReportParams;
  title?: string;
  chartType?: 'bar' | 'line' | 'table' | null;
}): Promise<DashboardPin & { existed: boolean }> {
  const r = await postFn<DashboardPin & { existed: boolean }>('aquastarDashboardPinAdd', input);
  window.dispatchEvent(new CustomEvent(PINS_CHANGED_EVENT));
  return r;
}

export async function removePin(id: string): Promise<void> {
  await postFn('aquastarDashboardPinRemove', { id });
  window.dispatchEvent(new CustomEvent(PINS_CHANGED_EVENT));
}

/** Compact rows for the model: what it needs to summarise, never the whole grid. */
export function reportDigest(r: ReportResult, maxRows = 15): Record<string, unknown> {
  return {
    report: r.report,
    title: r.title,
    subtitle: r.subtitle,
    params: r.params,
    totalRows: r.rows.length,
    columns: r.columns.map((c) => c.key),
    rows: r.rows.slice(0, maxRows).map((row) => {
      const o: Record<string, unknown> = {};
      for (const c of r.columns) o[c.key] = row[c.key];
      return o;
    }),
  };
}
